import { useEffect, useId, useRef, useState } from "react";
import type { TimeTrackEntry } from "@shared/timetrack";
import { useTimeTrackAccess, useTimeTrackMutation, useTimeTrackOnline } from "@/hooks/use-timetrack";
import { useSelectAllOnFocus } from "./select-on-focus";
import { exactDuration, minuteDuration, needsForeignConfirmation, parseDuration } from "./time-utils";

type Phase = "view" | "edit" | "confirm";

/** One click on a recorded duration turns it into an H:MM field. Enter or leaving saves; Escape cancels. */
export function InlineDuration({ entry, seconds, className = "" }: {
  entry: TimeTrackEntry; seconds: number; className?: string;
}) {
  const { user } = useTimeTrackAccess();
  const online = useTimeTrackOnline();
  const mutation = useTimeTrackMutation();
  const [phase, setPhase] = useState<Phase>("view");
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const viewRef = useRef<HTMLButtonElement>(null);
  const boxRef = useRef<HTMLSpanElement>(null);
  const restoreFocus = useRef(false);
  const select = useSelectAllOnFocus();
  const errorId = useId();
  const display = exactDuration(seconds);
  const pendingRef = useRef(false);
  pendingRef.current = mutation.isPending;
  const editable = !entry.deletedAt && entry.state !== "running" && !!user;
  const foreign = !!user && needsForeignConfirmation(entry, user.id);

  useEffect(() => { if (phase === "edit") inputRef.current?.focus(); }, [phase]);
  // Radix dialogs close on Escape in a document capture listener, before React
  // sees the key. Escape here must only cancel this edit, so intercept on window first.
  useEffect(() => {
    if (phase === "view") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || !boxRef.current?.contains(document.activeElement)) return;
      e.preventDefault(); e.stopPropagation();
      if (!pendingRef.current) { restoreFocus.current = true; setError(""); setPhase("view"); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [phase]);
  useEffect(() => {
    if (phase === "view" && restoreFocus.current) { restoreFocus.current = false; viewRef.current?.focus(); }
  }, [phase]);

  if (!editable) {
    const reason = entry.deletedAt ? "Restore this entry to change its time" : entry.state === "running" ? "Pause the timer to correct this time" : undefined;
    return <strong className={`tt-mono ${className}`} title={reason}>{display}</strong>;
  }

  const begin = () => { setText(minuteDuration(entry.durationSeconds)); setError(""); mutation.reset(); setPhase("edit"); };
  const cancel = (refocus = true) => { restoreFocus.current = refocus; setError(""); setPhase("view"); };
  const parsed = parseDuration(text);
  const unchanged = !parsed.error && parsed.formatted === minuteDuration(entry.durationSeconds);
  const save = async () => {
    try {
      await mutation.mutateAsync({ action: "edit", id: entry.id, data: {
        version: entry.version, durationSeconds: parsed.seconds!, confirmForeignEntry: foreign,
      } });
      restoreFocus.current = true;
      setPhase("view");
    } catch {
      setPhase("edit");
      requestAnimationFrame(() => inputRef.current?.select());
    }
  };
  const commit = (fromBlur: boolean) => {
    if (mutation.isPending) return;
    if (parsed.error) {
      // Leaving an untouched-but-invalid field should not trap focus; it simply reverts.
      if (fromBlur) { cancel(false); return; }
      setError(parsed.error); return;
    }
    if (unchanged) { cancel(!fromBlur); return; }
    if (!online) { setError("You’re offline. Reconnect to save this change."); return; }
    if (foreign) { setPhase("confirm"); return; }
    void save();
  };

  if (phase === "view") {
    return <button ref={viewRef} type="button" className={`tt-mono tt-inline-duration ${className}`} onClick={begin}
      aria-label={`Duration ${display}. Click to change hours and minutes`} title="Click to change hours and minutes">
      {display}
    </button>;
  }

  if (phase === "confirm") {
    return <span ref={boxRef} className="tt-inline-duration-confirm" role="group" aria-label="Confirm change">
      <span>Change {entry.workerName}’s time to <strong className="tt-mono">{parsed.formatted}</strong>? They’ll be notified.</span>
      <span className="tt-row">
        <button type="button" className="tt-button tt-primary" autoFocus disabled={mutation.isPending || !online} onClick={() => void save()}>
          {mutation.isPending ? "Saving…" : "Change time"}</button>
        <button type="button" className="tt-button" disabled={mutation.isPending} onClick={() => cancel()}>Cancel</button>
      </span>
      {mutation.error && <span className="tt-error" role="alert">{mutation.error.message}</span>}
    </span>;
  }

  const message = error || mutation.error?.message;
  return <span ref={boxRef} className="tt-inline-duration-edit">
    <input ref={inputRef} className="tt-mono" type="text" inputMode="numeric" autoComplete="off" value={text}
      aria-label={`Duration for ${entry.targetLabel} in hours and minutes`} placeholder="1:30"
      aria-invalid={!!message} aria-describedby={message ? errorId : undefined} readOnly={mutation.isPending}
      onFocus={select.onFocus} onMouseUp={select.onMouseUp}
      onChange={e => { setText(e.target.value); setError(""); }}
      onKeyDown={e => {
        if (e.key === "Enter") { e.preventDefault(); commit(false); }
      }}
      onBlur={() => { select.onBlur(); commit(true); }} />
    {mutation.isPending && <span className="tt-subtle" role="status">Saving…</span>}
    {message && <span className="tt-error" id={errorId} role="alert">{message}</span>}
  </span>;
}
